import Button from 'components/Form/Button';
import Check from 'components/Form/Check';
import Toggle from 'components/Form/Toggle';
import { Icon } from 'components/Icon';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  ConcludeRenegotiation,
  Update,
  msgRenegociacaoCliente,
} from 'services/cobranca';
import { exposeStrapiError } from 'utils';
import { installmentLabel } from 'utils/billing';
import {
  optionsPeriodicidade,
  optionsRenegotiationTerm,
  optionsSN,
  optionsTacTab,
} from 'utils/options';
import { parseCurrency, parseDecimal, unparseCurrency } from 'utils/parsers';
import { calculaCobranca, taxaCET } from 'utils/simuladorFin';
import FormCore from '../../Form/Core';
import BasicTable from '../../Form/Table';
import WrapperTwoCol from '../WrapperTwoCol';
import SimulationSummary, {
  buildSimulationItems,
} from './components/SimulationSummary';
import {
  AddProposalContainer,
  ButtonsAndInputWrapper,
  CardBadge,
  CardHeader,
  CardSubtitle,
  CardTitle,
  CompanyButtonContainer,
  HeaderIcon,
  LastColumnSlot,
  OfferCard,
  OfferForm,
  WaiverNote,
  WaiverRow,
} from './styled';

// Ha entrada quando o valor e maior que zero; vazio ou zero, nao.
const hasEntry = (value) => Number(value) > 0;

// A entrada nao pode passar do Saldo Devedor (PMT+MORA), a divida das
// parcelas marcadas. Igual e aceito.
const entryAboveDebt = (entry, debt) =>
  hasEntry(entry) && Number(entry) > (Number(debt) || 0);

// Mora de um titulo: juros de atraso, multa e IOF sobre os juros.
const lateFeesOf = (row) =>
  (Number(row?.valor_juros_mora) || 0) +
  (Number(row?.valor_multa) || 0) +
  (Number(row?.valor_iof_juros) || 0);

// Dispensa da mora: a opcao so aparece com as duas flags, a do usuario e a da
// distribuidora (em cobranca_regras_gerais, que chega aqui como `taxasiof`).
// Sem a do usuario o backend devolve 403; sem a da distribuidora, 400.
const canWaiveLateFees = (user, dados) =>
  user?.dispensa_mora === true &&
  dados?.taxasiof?.permite_dispensa_mora === true;

const ENTRY_ABOVE_DEBT_MESSAGE =
  'O valor de entrada não pode ser maior que o Saldo Devedor (PMT+MORA).';

export default function BankingRenegociar({ dados, reload }) {
  const { user, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([dados.titulos]);
  const formOfer = useRef(null);
  const [allSelect, setAllSelect] = useState(false);
  const [confirma, setConfirma] = useState(dados.confirma_cliente);
  const [recalcula, setRecalcula] = useState(false);
  // Dispara o recalculo depois que a entrada acima do Saldo Devedor e zerada.
  const [entryResets, setEntryResets] = useState(0);
  // Dispensa da mora (ver canWaiveLateFees). Ligada, a mora dos titulos
  // marcados sai do Saldo Devedor, e com ele da PMT e do valor presente: o
  // backend nao recalcula nada, so grava. `waivedLateFees` e o total que saiu.
  const waiverAllowed = canWaiveLateFees(user, dados);
  const [waiveLateFees, setWaiveLateFees] = useState(false);
  const [waivedLateFees, setWaivedLateFees] = useState(0);
  // Resultado do calculo exibido no Resumo da Simulacao. Os valores tambem
  // ficam no form (setItem), de onde o payload e o CET leem.
  const [summary, setSummary] = useState({});

  const close = () => setModal(null);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      // O titulo de entrada nao compoe o saldo devedor e nao pode ser
      // renegociado, entao fica fora do Saldo a Vencer.
      const saldoVencerFilter = dados.titulos
        .filter((a) => a.status === 'aberto' && a.entrada !== true)
        .map((a) => {
          return a.saldo_devedor;
        });

      //filtra se ja nao existe o saldo a vencer para adicionar, caso nao exista adiciona o saldo a vencer como parcela para renegociar.
      if (!dados.titulos.some((a) => a.parcela === 'Saldo a Vencer')) {
        dados.titulos.push({
          id: 0,
          parcela: 'Saldo a Vencer',
          renegociar: dados.renegociacao_saldodevedor,
          valor_renegociar: undefined,
          status: 'vencido',
          valor_juros_mora: !saldoVencerFilter[0] ? 0 : saldoVencerFilter[0],
          capital: 0,
          data_baixa: null,
          data_vencimento: undefined,
          dias_vencido: null,
          saldo_devedor: !saldoVencerFilter[0] ? 0 : saldoVencerFilter[0],
          tipo_baixa: null,
          valor_iof_juros: 0,
          valor_juros_remun: 0,
          valor_multa: 0,
          valor_recebido: 0,
        });
      }
      const data = dados.titulos;
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.parcela || 0) + (a.parcela || 0));
      const next = sorted.filter((item) => item?.status !== 'baixado');
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [user]);

  //informacaoes da tabela.
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Parcela',
        ref: 'parcela',
        renderCell: ({ row }) => {
          const status = row?.status;
          const parcela = installmentLabel(row);
          if ((status === 'vencido') & (parcela === 'Saldo a Vencer')) {
            return parcela;
          } else {
            return <span style={{ color: 'blue' }}>{parcela}</span>;
          }
        },
      },
      { title: 'Dias Vencido', ref: 'dias_vencido' },
      {
        title: 'Vencimento',
        ref: 'data_vencimento',
        renderCell: ({ row }) => {
          const status = row?.status;
          const data = !row?.data_vencimento
            ? '-'
            : moment(row?.data_vencimento).format('L');
          if (status === 'vencido') {
            return <span style={{ color: 'red' }}>{data}</span>;
          }
          return data;
        },
      },
      { title: 'PMT', ref: 'pmttitulo' },
      {
        title: 'Mora',
        ref: 'mora',
        // Com a dispensa ligada, a mora das parcelas marcadas aparece riscada:
        // ela nao entra no Saldo Devedor. A linha Saldo a Vencer nao tem mora
        // (o valor dela e principal), entao nunca e riscada.
        renderCell: ({ row }) =>
          waiveLateFees &&
          row?.renegociar &&
          row?.parcela !== 'Saldo a Vencer' ? (
            <span
              title="Mora dispensada"
              style={{ textDecoration: 'line-through', color: '#718096' }}
            >
              {row?.mora}
            </span>
          ) : (
            row?.mora
          ),
      },
      {
        align: 'center',
        title: (
          <div
            style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Check
              align="center"
              onChange={async () => {
                if (confirma === false) {
                  let botao;
                  for (let todos of dados.titulos) {
                    // A entrada nao pode ser renegociada.
                    if (todos.status === 'vencido' && todos.entrada !== true) {
                      let check = !allSelect;
                      if (check === true) {
                        botao = true;
                        todos.renegociar = true;
                      } else {
                        botao = false;
                        todos.renegociar = false;
                      }
                    }
                  }

                  setRegisters([...dados.titulos]);
                  await setAllSelect(botao);

                  calc();
                  calcCet();
                }
              }}
              checked={allSelect}
            />
          </div>
        ),
        ref: 'renegociar',
        // A entrada aparece no extrato, mas sem opcao de renegociar.
        renderCell: ({ row }) =>
          row.entrada ? null : (
            <Check
              onChange={async () => {
                if (confirma === false) {
                  for (let linhaparcela of dados.titulos) {
                    if (linhaparcela.parcela === row.parcela) {
                      linhaparcela.renegociar = !linhaparcela.renegociar;
                    }
                  }
                  setRegisters([...dados.titulos]);

                  calc();
                  calcCet();
                }
              }}
              checked={row.renegociar}
              label={''}
              align="center"
            />
          ),
      },
    ];
    return baseColumns;
  }, [registers, waiveLateFees]);

  //variaveis que vao na tabela.
  const rows = useMemo(() => {
    return (registers || [])
      .filter((a) => a.status === 'vencido')
      .map((a) => ({
        id: a?.id,
        data_vencimento: a?.data_vencimento,
        parcela: a?.parcela,
        entrada: a?.entrada === true,
        pmttitulo: parseCurrency(a?.valor),
        pmttotal: parseCurrency(
          a?.valor_juros_remun +
            a?.capital +
            a?.valor_juros_mora +
            a?.valor_multa +
            a?.valor_iof_juros
        ),
        mora: parseCurrency(
          a?.valor_juros_mora + a?.valor_multa + a?.valor_iof_juros
        ),
        status: a?.status,
        renegociar: a?.entrada === true ? false : !!a?.renegociar,
        valor_juros_mora: a?.valor_juros_mora,
        valor_multa: a?.valor_multa,
        valor_juros_remun: a?.valor_juros_remun,
        capital: a?.capital,
        valor_iof_juros: a?.valor_iof_juros,
        valor_renegociar: a?.valor_renegociar,
        dias_vencido: !a?.dias_vencido ? '-' : a?.dias_vencido,
      }));
  }, [registers]);

  useEffect(() => init(), [init]);

  //campos que para fazer a renegociacao e ver informações
  const formOfertaInitial = [
    {
      ref: 'valor_renegociar',
      placeholder: 'Saldo Devedor (PMT+MORA)',
      tress: true,
      type: 'money',
      required: false,
      disabled: true,
      onBlur: () => {
        if (!formOfer.current) {
          return;
        }
        calc();
        calcCet();
      },
    },
    {
      // Valor presente da aba pmt_ren (D18): o valor futuro da carencia (D6)
      // mais as tarifas e o IOF. E a base sobre a qual a PMT e calculada.
      // Preenchido pelo calc, vai no payload como `valor_renegociado`.
      ref: 'valor_renegociado',
      placeholder: 'Valor Renegociado',
      tress: true,
      type: 'money',
      required: false,
      disabled: true,
    },
    {
      ref: 'valor_entrada',
      placeholder: 'Valor de Entrada (R$)',
      tress: true,
      type: 'money',
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }

        // Lido do form, e nao do argumento: o FormInput tambem repassa o blur
        // do DOM, que chega aqui com o evento no lugar do valor.
        const entry = formOfer.current.getItem('valor_entrada');
        // Entrada acima do Saldo Devedor e zerada. O zero vai tambem para o
        // `dados.renegociacao_entrada`, de onde o useEffect do recalculo
        // repoe o campo; e o calc nao e chamado aqui, porque leria o valor
        // antigo do form (o setItem so vale no proximo render) e o gravaria
        // de volta no `dados`. O useEffect recalcula com o zero.
        if (
          entryAboveDebt(entry, formOfer.current.getItem('valor_renegociar'))
        ) {
          toast.error(ENTRY_ABOVE_DEBT_MESSAGE, {
            // O blur chega duas vezes (ver acima); o id evita o toast dobrado.
            toastId: 'entry-above-debt',
          });
          dados.renegociacao_entrada = 0;
          formOfer.current.setItem(0, 'valor_entrada');
          syncEntryDate(0);
          // Contador, e nao o toggle do `recalcula`: com o blur dobrado, dois
          // toggles se anulam e o recalculo nao roda.
          setEntryResets((prev) => prev + 1);
          return;
        }
        syncEntryDate(entry);
        setRecalcula((prev) => !prev);
        await calc();
        await calcCet();
      },
    },
    {
      // Vencimento do titulo de entrada (parcela 0) que o backend cria na
      // cobranca nova. So fica habilitada com valor de entrada (ver
      // syncEntryDate). A obrigatoriedade e o intervalo (entre hoje e a Data
      // 1a Parcela) sao validados no saveConclui, e nao com `required` ou
      // `validate`: o calc chama getForm() a cada recalculo, e a validacao
      // dispararia o toast e devolveria o form vazio no meio da digitacao.
      ref: 'data_entrada',
      placeholder: 'Data da Entrada',
      tress: true,
      type: 'date',
      disabled: confirma || !hasEntry(dados?.renegociacao_entrada),
      transparent: true,
    },
    {
      ref: 'dias_carencia',
      placeholder: 'Data 1º Parcela',
      tress: true,
      type: 'date',
      disabled: confirma,
      transparent: true,
      onBlur: () => {
        if (!formOfer.current) {
          console.log('formOfer.current is null');
          return;
        }
        setRecalcula((prev) => !prev); //state to trigger useEffect pra recalcular quando a data de carencia mudar, da de usar em outros campos caso precise
        calc();
        calcCet();
      },
    },

    {
      ref: 'taxa',
      placeholder: 'Taxa %',
      tress: true,
      type: 'number',
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        setRecalcula((prev) => !prev);
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'periodicidade',
      placeholder: 'Periodicidade',
      tress: true,
      type: 'select',
      options: optionsPeriodicidade,
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }

        await calc();
        await calcCet();
      },
    },
    {
      ref: 'prazo',
      placeholder: 'Prazo',
      tress: true,
      type: 'select',
      options: optionsRenegotiationTerm,
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }

        await calc();
        await calcCet();
      },
    },
    {
      ref: 'tacSN',
      placeholder: '% TAC?',
      tress: true,
      type: 'select',
      options: optionsTacTab,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'seguroSN',
      placeholder: 'Seguro Prestamista?',
      tress: true,
      type: 'select',
      options: optionsSN,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'seguroAssisSN',
      placeholder: 'Seguro Assistência?',
      tress: true,
      type: 'select',
      options: optionsSN,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },

    {
      ref: 'iofSN',
      placeholder: 'IOF?',
      tress: true,
      type: 'select',
      options: optionsSN,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'tabSN',
      placeholder: '% Taxa Serviço?',
      tress: true,
      type: 'select',
      options: optionsTacTab,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'carteira',
      placeholder: 'Bancarizar?',
      tress: true,
      type: 'select',
      options: optionsSN,
      disabled: confirma,
    },
  ];

  const [formOferta, setFormOferta] = useState(formOfertaInitial);

  // A Data da Entrada acompanha o Valor de Entrada: habilitada quando ha
  // valor, e desabilitada e apagada quando ele e zerado ou limpo, para nao
  // ir no payload uma data de uma entrada que nao existe mais.
  const syncEntryDate = (value) => {
    const enabled = hasEntry(value);
    if (!enabled) {
      formOfer.current.setItem('', 'data_entrada');
    }
    setFormOferta((prev) =>
      prev.map((item) =>
        item.ref === 'data_entrada'
          ? { ...item, disabled: confirma || !enabled }
          : item
      )
    );
  };

  const atualizarPlaceholder = (ref, novoPlaceholder) => {
    setFormOferta((prev) =>
      prev.map((item) =>
        item.ref === ref ? { ...item, placeholder: novoPlaceholder } : item
      )
    );
  };

  //faz o calculos dos juros
  const calc = useCallback(async () => {
    const formValues = formOfer.current.getForm?.();
    const perc_iof = dados?.perc_iof;
    const perc_tac = dados?.perc_tac;
    const taxa = dados?.taxas;
    const taxas = dados?.taxasbanco;
    const taxasiof = dados?.taxasiof;
    const taxabanco = {
      perc_iof: perc_iof,
      perc_tac: perc_tac,
      taxas: taxa,
      taxasbanco: taxas,
    };
    if (!taxabanco) {
      return;
    }

    // Ajusta a taxa conforme o valor do campo taxa do formulário
    const taxaAjustada = {
      ...taxabanco,
      perc_taxa: parseFloat(formValues.taxa),
      perc_tabSN: parseFloat(formValues.tabSN),
      perc_tacSN: parseFloat(formValues.tacSN),
      taxasiof: taxasiof,
    };
    // Carencia em dias corridos de hoje ate a 1a parcela (E6 da aba pmt_ren).
    const carencia = moment(formValues.dias_carencia)
      .startOf('day')
      .diff(moment().startOf('day'), 'days');
    const resultados = calculaCobranca(
      { ...formValues, carencia },
      taxaAjustada
    );

    // Atualiza os campos do formulrio com os resultados calculados

    formOfer.current.setItem(parseCurrency(resultados.valorParcela), 'pmt');
    // Numero, como o valor_renegociar: vai direto para o payload.
    formOfer.current.setItem(resultados.valorPresente, 'valor_renegociado');
    formOfer.current.setItem(parseCurrency(resultados.valorIof), 'iof');
    formOfer.current.setItem(
      parseCurrency(resultados.valorSeguro),
      'valor_seguro'
    );
    formOfer.current.setItem(
      parseCurrency(resultados.valorSeguroAssis),
      'valor_seguroAssis'
    );
    formOfer.current.setItem(parseCurrency(resultados.valorTab), 'tab');
    formOfer.current.setItem(parseCurrency(resultados.valorTac), 'tac');
    setSummary((prev) => ({
      ...prev,
      pmt: parseCurrency(resultados.valorParcela),
      iof: parseCurrency(resultados.valorIof),
      seguro: parseCurrency(resultados.valorSeguro),
      seguroAssis: parseCurrency(resultados.valorSeguroAssis),
      tab: parseCurrency(resultados.valorTab),
      tac: parseCurrency(resultados.valorTac),
      tabPerc: formValues.tabSN,
      iofSN: formValues.iofSN,
    }));

    dados.renegociacao_periodicidade =
      formOfer.current.getItem('periodicidade');

    dados.renegociacao_data_1parcela =
      formOfer.current.getItem('dias_carencia');
    dados.carteira = formOfer.current.getItem('carteira');
    dados.renegociacao_entrada = formOfer.current.getItem('valor_entrada');

    dados.renegociacao_prazo = formOfer.current.getItem('prazo');
    dados.renegociacao_taxa = formOfer.current.getItem('taxa');
    dados.renegociacao_seguro =
      formOfer.current.getItem('seguroSN') == 'Sim' ? true : false;
    dados.renegociacao_seguro_assistencia =
      formOfer.current.getItem('seguroAssisSN') == 'Sim' ? true : false;
    dados.renegociacao_iof =
      formOfer.current.getItem('iofSN') == 'Sim' ? true : false;
    dados.renegociacao_tab = formOfer.current.getItem('tabSN');
    dados.renegociacao_tac = formOfer.current.getItem('tacSN');
  }, [registers, allSelect]);

  //calcula a cet
  const calcCet = async () => {
    let prazo = parseInt(formOfer.current.getItem('prazo'));
    let pmt = unparseCurrency(formOfer.current.getItem('pmt'));
    let valorFinan =
      formOfer.current.getItem('valor_renegociar') -
      formOfer.current.getItem('valor_entrada');
    // Sem parcela marcada a PMT e o financiado sao zero, a equacao do CET vira
    // 0/0 e o taxaCET devolve "NaN". Nesse caso o CET fica vazio, que o resumo
    // ja exibe como valor ausente.
    let cet =
      prazo > 0 && pmt > 0 && valorFinan > 0
        ? await taxaCET(prazo, pmt, valorFinan * -1)
        : null;
    if (!Number.isFinite(Number(cet))) {
      cet = null;
    }
    formOfer.current.setItem(cet, 'cet');
    setSummary((prev) => ({ ...prev, cet }));
  };

  //traz os dados dos campos e quando alterado refaz os calculos, caso seja vazio traz um padrao.
  useEffect(() => {
    //verifica nos array qual esta selecionado para somar.
    // Com a dispensa da mora, as parcelas entram so pela PMT. A linha Saldo a
    // Vencer fica de fora da dispensa: o valor dela vem em
    // `valor_juros_mora`, mas e principal, e nao mora.
    let waived = 0;
    const renegociar = rows.reduce((a, item) => {
      if (item.renegociar === true) {
        let valor;

        if (!item.valor_renegociar) {
          //valor = unparseCurrency(item.mora) + item?.capital
          const mora = unparseCurrency(item.mora);
          const waiveThis =
            waiverAllowed && waiveLateFees && item.parcela !== 'Saldo a Vencer';
          if (waiveThis) {
            waived += lateFeesOf(item);
          }
          valor = (waiveThis ? 0 : mora) + unparseCurrency(item.pmttitulo);
        } else {
          valor = item.valor_juros_mora;
        }
        return a + valor;
      }
      return a;
    }, 0);
    setWaivedLateFees(Math.round(waived * 100) / 100);
    const today = new Date();
    const data1parcela = moment(today).add(30, 'days').format('YYYY-MM-DD');

    const dataParcela = new Date(formOfer.current.getItem('dias_carencia'));
    const diacarencia =
      Math.floor(
        (dataParcela.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

    if (diacarencia < 0) {
      toast.error(
        'A data da primeira parcela não pode ser menor que a data atual'
      );
    }
    const valorEntrada = formOfer.current.getItem('valor_entrada');
    const taxa = parseDecimal(formOfer.current.getItem('taxa'));
    const calculo = (taxa / 30) * diacarencia;
    const valorRenegociar = (calculo / 100) * (renegociar - valorEntrada);
    const renegociarValor = valorRenegociar + renegociar;

    formOfer.current.setItem(renegociar, 'valor_renegociar');

    if (!formOfer.current.getItem('valor_entrada')) {
      formOfer.current.setItem(0, 'valor_entrada');
      formOfer.current.setItem(renegociarValor, 'valor_total');
    } else {
      const valorEntrada = formOfer.current.getItem('valor_entrada');
      formOfer.current.setItem(renegociarValor - valorEntrada, 'valor_total');
    }

    formOfer.current.setItem(
      !dados.carteira ? 'Não' : dados.carteira,
      'carteira'
    );
    formOfer.current.setItem(
      !dados.renegociacao_tab ? dados.taxasiof.tab : dados.renegociacao_tab,
      'tabSN'
    );
    formOfer.current.setItem(
      !dados.renegociacao_tac ? dados.taxasiof.tac : dados.renegociacao_tac,
      'tacSN'
    );
    formOfer.current.setItem(
      !dados.renegociacao_data_1parcela
        ? data1parcela
        : dados.renegociacao_data_1parcela,
      'dias_carencia'
    );
    formOfer.current.setItem(
      !dados?.renegociacao_entrada ? 0 : dados?.renegociacao_entrada,
      'valor_entrada'
    );
    // Prazo salvo na renegociacao, senao o da primeira taxa da distribuidora,
    // senao 6. Distribuidora sem taxa cadastrada chega com `taxas` vazio ou
    // ausente, e o `taxas[0].prazo` sem `?.` derrubava a tela.
    formOfer.current.setItem(
      String(dados?.renegociacao_prazo || dados?.taxas?.[0]?.prazo || 6),
      'prazo'
    );
    formOfer.current.setItem(
      !dados?.renegociacao_taxa
        ? dados?.taxa.replace('%', '')
        : dados?.renegociacao_taxa,
      'taxa'
    );
    formOfer.current.setItem(
      !dados?.renegociacao_periodicidade
        ? !dados?.periodicidade
          ? 30
          : dados?.periodicidade
        : dados?.renegociacao_periodicidade,
      'periodicidade'
    );
    // A escolha do operador (renegociacao_seguro, gravada no calc) vale
    // inclusive quando e `false`; o seguro do contrato original so entra
    // enquanto nao houver escolha. Antes o `!` tratava o `false` como
    // ausencia e o campo voltava para "Sim" a cada recalculo.
    formOfer.current.setItem(
      typeof dados.renegociacao_seguro === 'boolean'
        ? dados.renegociacao_seguro
          ? 'Sim'
          : 'Não'
        : dados.seguro
          ? 'Sim'
          : 'Não',
      'seguroSN'
    );
    formOfer.current.setItem(
      dados.renegociacao_seguro_assistencia === true ? 'Sim' : 'Não',
      'seguroAssisSN'
    );
    formOfer.current.setItem(
      dados.renegociacao_iof === true ? 'Sim' : 'Não',
      'iofSN'
    );

    const handler = async () => {
      setTimeout(async () => {
        await calc();
        await calcCet();
      }, 100);
    };
    handler();
  }, [registers, calc, allSelect, recalcula, entryResets, waiveLateFees]);

  //funcao do botao para enviar ao cliente.
  const saveCliente = async () => {
    const form = await formOfer.current.getForm?.();

    const id = dados?.id;
    try {
      const titulos_renego = rows
        .filter((item) => item.parcela !== 'Saldo a Vencer')
        .map((item) => {
          if (item.renegociar === true) {
            return {
              ...item,
              id: item?.id,
              parcela: item?.parcela,
              renegociar: item?.renegociar,
            };
          }
          return item;
        });
      //pega o titulo criado para pegar a informacao de selecionado
      const saldoaVencer = rows.filter(
        (item) => item.parcela === 'Saldo a Vencer'
      );
      //pega os titulos em aberto ainda.
      const titulos_aber = dados?.titulos.filter((a) => a.status !== 'vencido');
      //junta todos os titulos em um lugar para atualizar, sem apagar os que ja tem.
      const payloadParcelas = [...titulos_renego, ...titulos_aber];

      //payload para salvar na renegociacao e mandar para o cliente.
      const payloadCobranca = {
        id: dados?.id,
        renegociacao_taxa: Number(form?.taxa),
        renegociacao_periodicidade: Number(form?.periodicidade),
        renegociacao_prazo: Number(form?.prazo),
        renegociacao_seguro: form?.seguroSN === 'Sim' ? true : false,
        renegociacao_seguro_assistencia:
          form?.seguroAssisSN === 'Sim' ? true : false,
        renegociacao_iof: form?.iofSN === 'Sim' ? true : false,
        renegociacao_saldodevedor: saldoaVencer[0].renegociar,
        renegociacao_entrada: form?.valor_entrada,
        carteira: form?.carteira,
        renegociacao_data_1parcela: form?.dias_carencia,
        titulos: payloadParcelas,
        contato_cliente: new Date(),
        renegociacao_tab: form?.tabSN,
        renegociacao_tac: form?.tacSN,
      };

      // payload para envio de mensagem para cliente
      const payload = {
        valor_renegociar: parseCurrency(form.valor_total),
        valor_entrada: parseCurrency(form.valor_entrada),
        periodicidade: form.periodicidade,
        prazo: form.prazo,
        pmt: form.pmt,
        data_1parcela: moment(form.dias_carencia).format('L'),
        telefone: dados.telefone,
        nome: dados.customer,
        banco: dados.banco,
        distribuidor: dados.distributor,
        idcobranca: dados.id,
      };
      //verifica se o cliente ja confirmou a renegociação, se confirmou nao pode enviar nova mensagem
      if (confirma === true) {
        toast.error(
          'Cliente já confirmou a renegociação, não é possível enviar nova mensagem!'
        );
        return;
      }
      //verificar o valor total for negativo nao vai enviar
      if (form.valor_total <= 0) {
        toast.error('Valor renegociado invalido, verifique as informações!');
        setLoading(false);
        return;
      }

      if (!dados.telefone) {
        toast.error('Telefone do cliente não está cadastrado');
        setLoading(false);
        return;
      }

      if (form?.dias_carencia) {
        const formattedDate = moment(
          form?.dias_carencia,
          ['DD/MM/YYYY', 'YYYY-MM-DD'],
          true
        );
        // Verifica se a data é menor que hoje
        if (formattedDate.isBefore(moment().startOf('day'))) {
          toast.error(
            'A data da primeira parcela não pode ser menor que a data atual'
          );
          setLoading(false);
          return;
        }
      }

      const result = await msgRenegociacaoCliente({ data: payload }, id);
      const resultUpdate = await Update({ data: payloadCobranca }, id);

      toast.success('Enviado mensagem ao Cliente com sucesso!');
      close();
      reload?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  //funcao do botao para salvar e fechar.
  const saveFecha = async () => {
    const form = await formOfer.current.getForm?.();
    try {
      setLoading(true);
      //traz as alterações para os titulos removendo o titulo criado Saldo a Vencer
      const titulos_renego = rows
        .filter((item) => item.parcela !== 'Saldo a Vencer')
        .map((item) => {
          if (item.renegociar === true) {
            return {
              ...item,
              id: item?.id,
              parcela: item?.parcela,
              renegociar: item?.renegociar,
            };
          }
          return item;
        });
      //pega o titulo criado para pegar a informacao de selecionado
      const saldoaVencer = rows.filter(
        (item) => item.parcela === 'Saldo a Vencer'
      );
      //pega os titulos em aberto ainda.
      const titulos_aber = dados?.titulos.filter((a) => a.status !== 'vencido');
      //junta todos os titulos em um lugar para atualizar, sem apagar os que ja tem.
      const payloadParcelas = [...titulos_renego, ...titulos_aber];

      const id = dados?.id;
      const payloadCobranca = {
        id: dados?.id,
        renegociacao_taxa: Number(form?.taxa),
        renegociacao_periodicidade: Number(form?.periodicidade),
        renegociacao_prazo: Number(form?.prazo),
        renegociacao_seguro: form?.seguroSN === 'Sim' ? true : false,
        renegociacao_seguro_assistencia:
          form?.seguroAssisSN === 'Sim' ? true : false,
        renegociacao_iof: form?.iofSN === 'Sim' ? true : false,
        renegociacao_saldodevedor: saldoaVencer[0].renegociar,
        renegociacao_entrada: form?.valor_entrada,
        carteira: form?.carteira,
        renegociacao_data_1parcela: form?.dias_carencia,
        titulos: payloadParcelas,
        renegociacao_tab: form?.tabSN,
        renegociacao_tac: form?.tacSN,
      };

      //verificar o valor total for negativo nao vai enviar
      if (form.valor_total < 0) {
        toast.error(
          'Valor renegociado esta negativo, verifique as informações!'
        );
        setLoading(false);
        return;
      }

      if (form?.dias_carencia) {
        const formattedDate = moment(
          form?.dias_carencia,
          ['DD/MM/YYYY', 'YYYY-MM-DD'],
          true
        );
        // Verifica se a data é menor que hoje
        if (formattedDate.isBefore(moment().startOf('day'))) {
          toast.error(
            'A data da primeira parcela não pode ser menor que a data atual'
          );
          setLoading(false);
          return;
        }
      }
      const result = await Update({ data: payloadCobranca }, id);

      toast.success('Registro salvo com sucesso!');
      close();
      reload?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  //conclui a renegociacao.
  // Uma chamada so (POST /cobrancas/:id/renegociacao/concluir): o backend cria
  // a cobranca nova, baixa os titulos renegociados e atualiza a original numa
  // transacao. O modal manda apenas as escolhas do operador e os valores
  // calculados, nunca os titulos.
  const saveConclui = async () => {
    if (loading) return;
    const form = formOfer.current.getForm?.();
    if (!form) return;

    // A trava de confirmacao do cliente (confirma_cliente) foi removida a
    // pedido do time: com o Enviar para o Cliente oculto, nenhum contrato
    // novo chegaria a ser confirmado. Quando o envio voltar, avalie
    // restaurar a checagem (o backend tambem nao valida hoje).

    // Number + "> 0" barra o valor zerado e tambem o ausente.
    if (!(Number(form?.valor_renegociar) > 0)) {
      toast.error('Valor renegociado esta zerado!');
      return;
    }
    if (!(Number(form?.valor_total) > 0)) {
      toast.error('Valor renegociado invalido, verifique as informações!');
      return;
    }
    const firstDue = moment(
      form?.dias_carencia,
      ['DD/MM/YYYY', 'YYYY-MM-DD'],
      true
    );
    if (!firstDue.isValid()) {
      toast.error('Informe a data da primeira parcela.');
      return;
    }
    if (firstDue.isBefore(moment().startOf('day'))) {
      toast.error(
        'A data da primeira parcela não pode ser menor que a data atual'
      );
      return;
    }

    // Com entrada, o backend cria o titulo de entrada na cobranca nova e
    // exige a data dele. Sem pagamento ate o 3o dia util depois desse
    // vencimento, a renegociacao e estornada sozinha.
    const valorEntrada = Number(form?.valor_entrada) || 0;
    if (entryAboveDebt(valorEntrada, form?.valor_renegociar)) {
      toast.error(ENTRY_ABOVE_DEBT_MESSAGE);
      return;
    }
    let dataEntrada = null;
    if (valorEntrada > 0) {
      const entryDue = moment(
        form?.data_entrada,
        ['DD/MM/YYYY', 'YYYY-MM-DD'],
        true
      );
      if (!entryDue.isValid()) {
        toast.error('Informe a data da entrada.');
        return;
      }
      if (entryDue.isBefore(moment().startOf('day'))) {
        toast.error('A data da entrada não pode ser menor que a data atual.');
        return;
      }
      if (entryDue.isAfter(firstDue)) {
        toast.error(
          'A data da entrada não pode ser posterior à data da primeira parcela.'
        );
        return;
      }
      dataEntrada = entryDue.format('YYYY-MM-DD');
    }

    // A linha "Saldo a Vencer" e sintetica: nao vai em `parcelas`, vira o
    // `saldo_a_vencer`, que faz o backend incluir todos os titulos em aberto.
    const selected = rows.filter((item) => item.renegociar === true);
    const parcelas = selected
      .filter((item) => item.parcela !== 'Saldo a Vencer')
      .map((item) => Number(item.parcela));
    const saldoAVencer = selected.some(
      (item) => item.parcela === 'Saldo a Vencer'
    );

    const payload = {
      parcelas,
      saldo_a_vencer: saldoAVencer,
      data_1parcela: firstDue.format('YYYY-MM-DD'),
      prazo: Number(form?.prazo),
      periodicidade: Number(form?.periodicidade),
      valor_pmt: unparseCurrency(form?.pmt),
      // PENDENTE: o negocio ainda decide entre o valor_total do modal e o
      // valor presente (D18 da aba pmt_ren). Por ora segue o valor_total.
      valor_financiado: Number(form?.valor_total),
      // Saldo Devedor (PMT+MORA): a divida que entra na renegociacao. Ja
      // validado como > 0 acima.
      valor_saldo_devedor: Number(form?.valor_renegociar),
      // Valor presente (D18): valor futuro da carencia + tarifas + IOF.
      valor_renegociado: Number(form?.valor_renegociado) || 0,
      taxa: parseDecimal(form?.taxa),
      valor_entrada: valorEntrada,
      // Sem entrada vai null: o backend ignora.
      data_entrada: dataEntrada,
      tac: Number(form?.tacSN) || 0,
      tab: Number(form?.tabSN) || 0,
      seguro: form?.seguroSN === 'Sim',
      seguro_assistencia: form?.seguroAssisSN === 'Sim',
      iof: form?.iofSN === 'Sim',
      carteira: form?.carteira,
      // Os valores acima ja chegam sem a mora quando ela e dispensada: o
      // backend so grava a escolha e marca os titulos com `mora_dispensada`.
      dispensa_mora: waiverAllowed && waiveLateFees,
    };

    setLoading(true);
    try {
      const result = await ConcludeRenegotiation(dados?.id, { data: payload });
      if (exposeStrapiError(result)) return;

      // Renegociacao parcial: parte dos titulos continua em aberto na
      // original e segue sendo cobrada pela regua.
      const waivedNote =
        Number(result?.mora_dispensada_total) > 0
          ? ` Mora dispensada: ${parseCurrency(
              Number(result.mora_dispensada_total)
            )}.`
          : '';
      if (result?.titulos_restantes > 0) {
        toast.success(
          `Renegociação concluída. ${result.titulos_restantes} título(s) continuam em aberto na cobrança original.${waivedNote}`
        );
      } else {
        toast.success(`Renegociação concluída!${waivedNote}`);
      }
      close();
      reload?.();
    } finally {
      setLoading(false);
    }
  };

  if (!registers) {
    return null;
  }

  return (
    <WrapperTwoCol
      title={'Renegociação'}
      titleColumn1="Extrato"
      width="80vw"
      children={
        <AddProposalContainer>
          <BasicTable columns={columns} rows={rows} loading={loading} />
        </AddProposalContainer>
      }
      children2={
        <AddProposalContainer>
          <OfferCard>
            <CardHeader>
              <HeaderIcon>
                <Icon name="calculate" size={22} />
              </HeaderIcon>
              <div>
                <CardTitle>Oferta</CardTitle>
                <CardSubtitle>
                  Preencha os dados da simulação para gerar a proposta.
                </CardSubtitle>
              </div>
              <CardBadge>Simulação</CardBadge>
            </CardHeader>
            <OfferForm>
              <FormCore ref={formOfer} formItems={formOferta} />
              {waiverAllowed ? (
                <WaiverRow>
                  <Toggle
                    label="Dispensar mora"
                    checked={waiveLateFees}
                    onChange={(value) => {
                      if (!confirma) setWaiveLateFees(value);
                    }}
                  />
                  {waiveLateFees ? (
                    <WaiverNote>
                      Mora dispensada: {parseCurrency(waivedLateFees)}. Juros,
                      multa e IOF de atraso das parcelas marcadas não entram no
                      Saldo Devedor.
                    </WaiverNote>
                  ) : null}
                </WaiverRow>
              ) : null}
            </OfferForm>
          </OfferCard>
          <SimulationSummary
            pmt={summary?.pmt}
            items={buildSimulationItems(summary)}
          />
        </AddProposalContainer>
      }
      footer={
        <div style={{ display: 'grid', width: '100%', gap: '20px' }}>
          <ButtonsAndInputWrapper>
            <CompanyButtonContainer>
              {/* OCULTOS POR ORA, a pedido do time: Enviar para o Cliente e
                  Fechar e Salvar voltam a ser usados mais adiante. As funcoes
                  (saveCliente, saveFecha) continuam no arquivo; para
                  reativar, basta descomentar os dois botoes.
              <Button color="secondary" onClick={saveCliente} nospace>
                {loading ? 'Processando' : 'Enviar para o Cliente'}
              </Button>
              <Button onClick={saveFecha} color="blue" nospace>
                {loading ? 'Processando' : 'Fechar e Salvar'}
              </Button>
              */}
              <LastColumnSlot>
                <Button
                  onClick={saveConclui}
                  color="green"
                  nospace
                  disabled={loading}
                >
                  {loading ? 'Processando' : 'Fechar e Concluir'}
                </Button>
              </LastColumnSlot>
            </CompanyButtonContainer>
          </ButtonsAndInputWrapper>
        </div>
      }
    ></WrapperTwoCol>
  );
}
